import { useCallback, useMemo, useState } from 'react'
import Fuse, { type FuseOptionKey, type IFuseOptions } from 'fuse.js'
import { useDebounce } from '@dhl/utils'

export type Options<T> = IFuseOptions<T> & {
  debounce?: number
}

type Args<T> = {
  searchableItems: T[]
  searchKeys: FuseOptionKey<T>[]
  options?: Options<T>
}

type SearchResults<T> = {
  debouncedSearchTerm: string
  onSearchTermChange: (term: string) => void
  onSearchTermClear: () => void
  results: T[]
  searchTerm: string
}

const defaultOptions: Partial<Options<unknown>> = {
  fieldNormWeight: 1,
  shouldSort: false,
  threshold: 0.3,
  debounce: 500,
}

export const useSearch = <T>({ options: optionsFromArgs, searchKeys, searchableItems }: Args<T>): SearchResults<T> => {
  const [searchTerm, setSearchTerm] = useState('')

  const onSearchTermChange = useCallback((term: string) => setSearchTerm(term), [])
  const onSearchTermClear = useCallback(() => setSearchTerm(''), [])

  const { debounce, ...fuseOptions } = { ...defaultOptions, ...optionsFromArgs }

  const debouncedSearchTerm = useDebounce(searchTerm, debounce!)

  const results = useMemo(() => {
    const fuse = new Fuse(searchableItems ?? [], {
      ...fuseOptions,
      keys: searchKeys,
    })

    return (debouncedSearchTerm ? fuse.search(debouncedSearchTerm).map((result) => result.item) : searchableItems) ?? []
  }, [fuseOptions, searchKeys, debouncedSearchTerm, searchableItems])

  return {
    debouncedSearchTerm,
    onSearchTermChange,
    onSearchTermClear,
    results,
    searchTerm,
  }
}
